import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Coins, CalendarClock, ShieldCheck, Sparkles, ShoppingCart, Loader2, CheckCircle2,
} from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 20;

export default function BuyCredits() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [purchasing, setPurchasing] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollStatus, setPollStatus] = useState(null); // 'pending' | 'paid' | 'expired' | 'failed'

  const limit = user?.daily_limit ?? 5;
  const windowHrs = user?.window_hours ?? 12;
  const usedToday = user?.scans_used_today ?? 0;
  const remaining = user?.scans_remaining_today ?? limit;
  const bonus = user?.bonus_scans ?? 0;

  const sessionIdFromUrl = params.get("session_id");
  const canceled = params.get("canceled");

  // ---- polling on return from Stripe ----
  const pollSession = useCallback(async (sessionId) => {
    setPolling(true);
    setPollStatus("pending");
    let attempts = 0;
    let retrieveUnavailableCount = 0;
    while (attempts < POLL_MAX_ATTEMPTS) {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPollStatus("paid");
          await refresh?.();
          toast.success("Payment successful — 5 scans added!");
          setPolling(false);
          params.delete("session_id");
          setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired") {
          setPollStatus("expired");
          toast.error("Payment session expired. Try again.");
          setPolling(false);
          return;
        }
        if (data.retrieve_unavailable) {
          // Emergent test-mode proxy can't retrieve sessions — rely on webhook.
          retrieveUnavailableCount += 1;
        }
      } catch (e) {
        // ignore transient errors, keep polling
      }
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      attempts += 1;
    }
    if (retrieveUnavailableCount >= 3) {
      setPollStatus("retrieve_unavailable");
      toast.message(
        "Payment confirmation pending. If you completed checkout, refresh in a minute — Stripe's webhook will credit your scans."
      );
    } else {
      setPollStatus("failed");
      toast.error("We couldn't confirm your payment. If you were charged, please email Support.");
    }
    setPolling(false);
  }, [params, setParams, refresh]);

  useEffect(() => {
    if (sessionIdFromUrl) {
      pollSession(sessionIdFromUrl);
    } else if (canceled) {
      toast.info("Payment canceled. No charges made.");
      params.delete("canceled");
      setParams(params, { replace: true });
    }
  }, [sessionIdFromUrl, canceled]);

  const handleBuy = async () => {
    setPurchasing(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        pack_id: "scans_5",
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't start checkout. Try again.");
        setPurchasing(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout.");
      setPurchasing(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16 animate-fade-in">
      <p className="cm-label">YOUR SCANS</p>
      <h1 className="cm-heading text-4xl sm:text-5xl mt-3">
        Free <span className="text-copper">{limit}</span> scans, every {windowHrs} hours.
      </h1>
      <p className="mt-4 text-gray-400 max-w-xl leading-relaxed">
        Every account gets {limit} free scans on a rolling {windowHrs}-hour window. Want more?
        Top up with a 5-scan pack for $2.99 — scans never expire.
      </p>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="cm-card p-6">
          <div className="flex items-center gap-3">
            <Coins className="w-5 h-5 text-copper" />
            <p className="cm-label">Free allowance</p>
          </div>
          <p className="cm-heading text-5xl mt-3" data-testid="buy-credits-balance">
            {remaining} <span className="text-2xl text-gray-500">/ {limit}</span>
          </p>
          <p className="text-xs text-gray-500 mt-2">
            {usedToday} used in the last {windowHrs}h
          </p>
        </div>
        <div className="cm-card p-6 border-copper/30">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-5 h-5 text-copper" />
            <p className="cm-label">Top-up balance</p>
          </div>
          <p className="cm-heading text-5xl mt-3" data-testid="bonus-balance">
            {bonus}
          </p>
          <p className="text-xs text-gray-500 mt-2">Purchased scans — never expire</p>
        </div>
      </div>

      {/* Purchase block */}
      <div
        className="mt-10 cm-card p-8 border-copper/40 bg-gradient-to-br from-ink-900/40 to-ink-950"
        data-testid="purchase-pack-card"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="cm-label text-copper">5 SCAN PACK</p>
            <p className="cm-heading text-4xl sm:text-5xl mt-2">$2.99</p>
            <p className="text-sm text-gray-400 mt-2 max-w-md leading-relaxed">
              Adds 5 scans to your account. Use them anytime — they never expire.
              Buy as many packs as you want.
            </p>
          </div>
          <button
            type="button"
            onClick={handleBuy}
            disabled={purchasing || polling}
            data-testid="buy-pack-button"
            className="cm-btn-primary inline-flex items-center justify-center gap-2 w-full md:w-auto"
          >
            {purchasing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Starting checkout…
              </>
            ) : polling && pollStatus === "pending" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Confirming payment…
              </>
            ) : pollStatus === "paid" ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-green-400" /> Scans added!
              </>
            ) : (
              <>Buy 5 scans</>
            )}
          </button>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="cm-card p-6">
          <CalendarClock className="w-5 h-5 text-copper" />
          <p className="cm-label mt-4">Rolling window</p>
          <p className="text-sm text-gray-400 mt-2 leading-relaxed">
            Your free {limit} scans refresh on a sliding {windowHrs}-hour window as old scans age out.
          </p>
        </div>
        <div className="cm-card p-6">
          <Sparkles className="w-5 h-5 text-copper" />
          <p className="cm-label mt-4">Every result saved</p>
          <p className="text-sm text-gray-400 mt-2 leading-relaxed">
            All your scan results stay in your History — they never expire.
          </p>
        </div>
        <div className="cm-card p-6">
          <ShieldCheck className="w-5 h-5 text-copper" />
          <p className="cm-label mt-4">Secure checkout</p>
          <p className="text-sm text-gray-400 mt-2 leading-relaxed">
            All payments processed by Stripe. We never see or store your card details.
          </p>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-4">
        <Link to="/scan" className="cm-btn-primary inline-flex" data-testid="buy-credits-to-scan">
          Scan a haircut
        </Link>
        <Link to="/dashboard" className="cm-btn-secondary inline-flex">
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}
